import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Flame, Loader2, Minus, Plus, Search, Share2, ShoppingBag, Sparkles, Trash2 } from "lucide-react";import { toast } from "sonner";
import { API, apiError, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, ErrorState, Field, Loading } from "@/components/ds";
import { SafeImage } from "@/components/ds/SafeImage";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { ProcessingOverlay } from "@/components/ds/ProcessingOverlay";
import { BannerCarousel } from "@/components/ds/BannerCarousel";
import { CustomizeModal } from "@/components/catalog/CustomizeModal";
import { DeliveryEstimateCard, useDeliveryEstimate } from "@/components/ds/DeliveryEstimateCard";

const publicApi = axios.create({ baseURL: API });

/** #RRGGBB -> "h s% l%", o formato das variáveis de cor do app. Valor inválido é ignorado. */
function hexToHslParts(hex) {
  const hsl = hexToHsl(hex);
  return hsl ? `${hsl.h} ${hsl.s}% ${hsl.l}%` : null;
}

function hexToHsl(hex) {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex || "")) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  if (d) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = d ? d / (1 - Math.abs(2 * l - 1)) : 0;
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

const asParts = (hsl) => `${hsl.h} ${hsl.s}% ${hsl.l}%`;

/** Mistura duas cores em HSL para gerar bordas e textos de apoio legíveis. */
function mixHsl(a, b, ratio) {
  return { h: Math.round(a.h + (b.h - a.h) * ratio),
           s: Math.round(a.s + (b.s - a.s) * ratio),
           l: Math.round(a.l + (b.l - a.l) * ratio) };
}

// Texto do botão: claro sobre cor escura, escuro sobre cor clara.
const contrastParts = (hsl) => (hsl.l > 62 ? "222 40% 10%" : "0 0% 100%");

const cartKey = (id, variationId) => (variationId ? `${id}|${variationId}` : id);

// Carrinho guardado no navegador, separado por contexto de catálogo (principal x cada criadora).
const storageKey = (slug) => `tr3nd.cart.${slug ? `creator:${slug}` : "principal"}`;
const BUYER_KEY = "tr3nd.buyer";

const readStore = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeStore = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* navegador sem armazenamento disponível: o carrinho segue só na sessão atual */
  }
};
const mediaUrl = (url) => (url ? `${process.env.REACT_APP_BACKEND_URL}${url}` : null);

// Compara hash e nome de categoria ignorando acento, caixa, espaço e pontuação:
// #portalatas, #PortaLatas e #porta-latas chegam todos em "portalatas".
const slugKey = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

// Âncoras que já existem na página e seguem com o comportamento nativo de scroll.
const ANCHOR_HASHES = ["produtos", "kits"];

export default function Catalog() {
  // Link direto: /catalogo/p/<slug>?c=<sku-da-combinação>. Mesmo catálogo, em modo foco.
  const focusSlug = useParams().slug || "";
  const creatorSlug = useParams().creatorSlug || "";
  const focusVariation = new URLSearchParams(window.location.search).get("c") || "";
  const [products, setProducts] = useState(null);
  const [config, setConfig] = useState({ studio_name: "TR3ND Studio", whatsapp: "", catalog_message: "" });
  const [creator, setCreator] = useState(null);
  const [error, setError] = useState("");
  const [cart, setCart] = useState(() => readStore(storageKey(creatorSlug), {}).cart || {});
  // Personalização por linha do carrinho: cada item guarda a sua.
  const [customizations, setCustomizations] = useState(
    () => readStore(storageKey(creatorSlug), {}).customizations || {}
  );
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [search, setSearch] = useState(
    new URLSearchParams(window.location.search).get("busca") || ""
  );
  const [activeCategory, setActiveCategory] = useState("Todos");

  const load = () => {
    setError("");
    const catalogPath = creatorSlug
      ? `/public/catalog?creator=${encodeURIComponent(creatorSlug)}`
      : "/public/catalog";
    const extra = creatorSlug
      ? publicApi.get(`/public/creator-catalog/${encodeURIComponent(creatorSlug)}`)
      : Promise.resolve({ data: null });
    Promise.all([publicApi.get(catalogPath), publicApi.get("/public/config"), extra])
      .then(([c, cfg, cr]) => {
        setProducts(c.data);
        setConfig(cfg.data);
        setCreator(cr.data);
      })
      .catch((e) => setError(apiError(e)));
  };

  useEffect(load, [creatorSlug]);

  // Troca de catálogo: carrega o carrinho daquele contexto, sem apagar o do outro.
  const context = useRef(creatorSlug);
  useEffect(() => {
    if (context.current === creatorSlug) return;
    context.current = creatorSlug;
    const saved = readStore(storageKey(creatorSlug), {});
    setCart(saved.cart || {});
    setCustomizations(saved.customizations || {});
  }, [creatorSlug]);

  useEffect(() => {
    writeStore(storageKey(creatorSlug), { cart, customizations });
  }, [cart, customizations, creatorSlug]);

  // Item restaurado só sai do carrinho se o produto saiu mesmo do catálogo. Sem estoque não
  // remove nada: o pedido entra para produção, como sempre.
  const checked = useRef(null);
  useEffect(() => {
    if (!products || checked.current === creatorSlug) return;
    checked.current = creatorSlug;
    const gone = Object.keys(cart).filter(
      (key) => !products.some((p) => p.id === key.split("|")[0])
    );
    if (gone.length === 0) return;
    setCart((c) => {
      const next = { ...c };
      gone.forEach((key) => delete next[key]);
      return next;
    });
    setCustomizations((prev) => {
      const next = { ...prev };
      gone.forEach((key) => delete next[key]);
      return next;
    });
    toast.info(
      gone.length === 1
        ? "Um item saiu do catálogo e foi retirado do seu carrinho."
        : `${gone.length} itens saíram do catálogo e foram retirados do seu carrinho.`
    );
  }, [products, cart, creatorSlug]);

  const clearCart = () => {
    if (!window.confirm("Limpar o carrinho? Todos os itens escolhidos serão removidos.")) return;
    setCart({});
    setCustomizations({});
  };

  // Tema da criadora: hex vira o mesmo formato HSL usado pelas variáveis do app,
  // então todas as classes existentes (fundo, cartão, texto, brand/primary) seguem a cor dela.
  const themeStyle = useMemo(() => {
    const theme = (creator ? creator.theme : config.theme) || {};
    const vars = {};
    const primary = hexToHslParts(theme.primary_color);
    const accent = hexToHslParts(theme.accent_color);
    const button = hexToHsl(theme.button_color);
    const background = hexToHsl(theme.background_color);
    const card = hexToHsl(theme.card_color);
    const surface = hexToHsl(theme.surface_color);
    const text = hexToHsl(theme.text_color);
    if (primary) vars["--brand"] = primary;
    if (accent) vars["--info"] = accent;
    if (button) {
      vars["--primary"] = asParts(button);
      vars["--primary-foreground"] = contrastParts(button);
    } else if (primary) {
      vars["--primary"] = primary;
    }
    if (background) vars["--background"] = asParts(background);
    if (card) {
      vars["--card"] = asParts(card);
      vars["--card-raised"] = asParts(card);
      vars["--popover"] = asParts(card);
    }
    if (surface) {
      ["--secondary", "--muted", "--accent"].forEach((v) => {
        vars[v] = asParts(surface);
      });
    }
    if (text) {
      ["--foreground", "--card-foreground", "--popover-foreground",
       "--secondary-foreground", "--accent-foreground"].forEach((v) => {
        vars[v] = asParts(text);
      });
      const base = background || card;
      if (base) {
        // Texto de apoio e bordas nascem da mistura texto/fundo: contraste garantido
        // tanto em fundo claro quanto escuro.
        vars["--muted-foreground"] = asParts(mixHsl(text, base, 0.42));
        vars["--border"] = asParts(mixHsl(base, text, 0.22));
        vars["--input"] = vars["--border"];
      }
    }
    return vars;
  }, [creator, config]);

  // Aplicado na raiz para alcançar também os modais (portais) desta página; removido ao sair.
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(themeStyle).forEach(([key, value]) => root.style.setProperty(key, value));
    return () => Object.keys(themeStyle).forEach((key) => root.style.removeProperty(key));
  }, [themeStyle]);


  const items = useMemo(
    () =>
      Object.entries(cart)
        .map(([key, qty]) => {
          const [id, vid] = key.split("|");
          const product = (products || []).find((p) => p.id === id);
          const variation = (product?.variations || []).find((v) => v.id === vid) || null;
          return { key, product, variation, quantity: qty,
                   customization: customizations[key] || [] };
        })
        .filter((i) => i.product),
    [cart, products, customizations]
  );
  const total = items.reduce((s, i) => s + i.product.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  // Prazo automático: o mesmo payload do checkout vai ao motor de prazo do backend.
  const payloadItems = useMemo(
    () =>
      items.map((i) =>
        i.product.kind === "kit"
          ? { kit_id: i.product.id, variation_id: i.variation?.id || null, quantity: i.quantity }
          : {
              product_id: i.product.id,
              variation_id: i.variation?.id || null,
              quantity: i.quantity,
              customization: i.customization || [],
            }
      ),
    [items]
  );
  const eta = useDeliveryEstimate(
    () =>
      publicApi
        .post("/public/delivery-estimate", { items: payloadItems })
        .then((r) => r.data),
    JSON.stringify(payloadItems),
    payloadItems.length > 0 && (cartOpen || checkoutOpen)
  );

  const add = (key, customization) =>
    setCart((c) => {
      if (customization) {
        setCustomizations((prev) => ({ ...prev, [key]: customization }));
      }
      return { ...c, [key]: (c[key] || 0) + 1 };
    });
  const dec = (key) =>
    setCart((c) => {
      const next = { ...c, [key]: (c[key] || 0) - 1 };
      if (next[key] <= 0) {
        delete next[key];
        setCustomizations((prev) => {
          const rest = { ...prev };
          delete rest[key];
          return rest;
        });
      }
      return next;
    });
  const remove = (key) =>
    setCart((c) => {
      const next = { ...c };
      delete next[key];
      return next;
    });

  const all = products || [];
  // Kit sem categoria própria continua na vitrine "Kits"; com categoria, vira produto normal.
  const catOf = (p) => (p.kind === "kit" ? p.category || "Kits" : p.category || "Outros");
  const kits = all.filter((p) => p.kind === "kit" && !p.category);

  // Categorias vêm dos próprios produtos cadastrados; nada é inventado.
  const categoryNames = useMemo(() => {
    const names = [];
    all.forEach((p) => {
      const key = catOf(p);
      if (!names.includes(key)) names.push(key);
    });
    return ["Todos", ...names];
  }, [all]);

  const term = search.trim().toLowerCase();
  const visible = useMemo(
    () =>
      all.filter((p) => {
        const cat = catOf(p);
        const okCat = activeCategory === "Todos" || cat === activeCategory;
        const okTerm =
          !term ||
          p.name.toLowerCase().includes(term) ||
          (p.description || "").toLowerCase().includes(term) ||
          (p.category || "").toLowerCase().includes(term);
        return okCat && okTerm;
      }),
    [all, activeCategory, term]
  );

  // Destaques: o que o admin marcou manualmente vem primeiro. Sem marcação,
  // cai na regra automática (pronta entrega primeiro, depois maior valor).
  const bestSellers = useMemo(() => {
    const manual = all.filter((p) => p.featured && p.kind !== "kit");
    if (manual.length) return manual.slice(0, 6);
    return [...all]
      .filter((p) => p.kind !== "kit")
      .sort((a, b) => Number(b.available) - Number(a.available) || b.price - a.price)
      .slice(0, 3);
  }, [all]);

  // Banner com #categoria vira filtro real: o hash é resolvido contra as MESMAS
  // categoryNames usadas nos chips. Hash que não casa com categoria não muda nada.
  useEffect(() => {
    if (focusSlug || categoryNames.length <= 1) return;
    const applyHash = () => {
      let raw = window.location.hash.replace(/^#/, "");
      try {
        raw = decodeURIComponent(raw);
      } catch {
        /* hash malformado: usa como veio */
      }
      const key = slugKey(raw);
      if (!key || ANCHOR_HASHES.includes(key)) return;
      const noS = (s) => s.replace(/s$/, "");
      const match =
        categoryNames.find((c) => slugKey(c) === key) ||
        categoryNames.find((c) => noS(slugKey(c)) === noS(key));
      if (!match) return;
      setActiveCategory(match);
      document.getElementById("produtos")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, [categoryNames, focusSlug]);

  const focused = focusSlug ? all.find((p) => p.slug === focusSlug) || null : null;  const focusedVariationId = focused
    ? (focused.variations || []).find(
        (v) => v.sku === focusVariation || v.id === focusVariation
      )?.id || ""
    : "";

  const showKitsSection =
    !focusSlug && kits.length > 0 && activeCategory === "Todos" && !term;

  const categories = useMemo(() => {
    const map = {};
    visible.forEach((p) => {
      // Kit sem categoria própria fica na vitrine "Kits em destaque": não repetir o card.
      if (p.kind === "kit" && !p.category && showKitsSection) return;
      const key = catOf(p);
      map[key] = map[key] || [];
      map[key].push(p);
    });
    return Object.entries(map);
  }, [visible, showKitsSection]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="catalog-page" style={themeStyle}>
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-5 py-3.5 md:px-8">
          <div className="flex flex-1 items-center gap-2.5">
            <img
              src="/brand/tr3nd-logo.jpg"
              alt={config.studio_name}
              data-testid="catalog-logo"
              className="h-10 w-10 shrink-0 rounded-lg object-contain sm:h-12 sm:w-12"
            />
            <div>
              <p className="font-display text-base font-semibold leading-none">
                {creator?.display_name || config.studio_name}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {creator ? `Coleção autoral · ${config.studio_name}`
                         : "Impressão 3D · Peças autorais"}
              </p>
            </div>
          </div>

          <div className="relative order-3 w-full md:order-2 md:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar produto ou kit..."
              className="h-10 rounded-full pl-9"
              data-testid="catalog-search-input"
            />
          </div>
          <Button
            onClick={() => setCartOpen(true)}
            data-testid="open-cart-btn"
            className="relative order-2 h-11 gap-2 rounded-full px-5 font-semibold transition-transform active:scale-95 md:order-3"
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">Meu pedido</span>
            {count > 0 && (
              <span
                className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1 font-num text-[10px] font-bold text-brand-foreground"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 pb-32 pt-8 md:px-8 md:pb-16">
        {focusSlug ? (
          <div data-testid="catalog-focus">
            <a
              href="/catalogo"
              data-testid="catalog-focus-back"
              className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Ver catálogo completo
            </a>
            {products === null && !error && <Loading label="Carregando o produto..." />}
            {error && <ErrorState message={error} onRetry={load} />}
            {products && !focused && (
              <EmptyState
                icon={ShoppingBag}
                title="Produto não disponível"
                description="Este produto não está publicado no catálogo neste momento."
              />
            )}
            {focused && (
              <div className="max-w-md">
                <ProductCard
                  p={focused}
                  cart={cart}
                  add={add}
                  dec={dec}
                  section="focus"
                  initialVariationId={focusedVariationId}
                  creatorSlug={creatorSlug}
                />
              </div>
            )}
          </div>
        ) : (
        <>
        <BannerCarousel
          banners={creator
            ? (creator.banners?.length
                ? creator.banners
                : ((creator.headline || creator.tagline)
                    ? [{ id: "creator-hero", title: creator.headline,
                         subtitle: creator.tagline }]
                    : []))
            : (config.banners || [])}
          fallbackTitle={creator ? creator.headline
                                 : (config.texts?.catalog_title || "")}
          fallbackSubtitle={creator
            ? creator.tagline
            : (config.texts?.catalog_subtitle || config.catalog_message || "")}
          hasKits={kits.length > 0}
        />

        {categoryNames.length > 2 && (
          <div className="mb-8 flex flex-wrap gap-2" data-testid="catalog-categories">
            {categoryNames.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCategory(c)}
                data-testid={`catalog-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`tr-chip ${activeCategory === c ? "tr-chip-active" : ""}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {products === null && !error && <Loading label="Carregando o catálogo..." />}
        {error && <ErrorState message={error} onRetry={load} />}
        {products && products.length === 0 && (
          <EmptyState
            icon={ShoppingBag}
            title="Catálogo em preparação"
            description="Em breve novos produtos por aqui."
          />
        )}

        {showKitsSection && (
          <section className="mb-12" id="kits" data-testid="catalog-kits-section">
            <h2 className="mb-4 flex items-center gap-2 font-display text-base font-medium md:text-lg">
              <Sparkles className="h-4 w-4 text-brand" /> Kits em destaque
            </h2>
            <div className="grid grid-cols-2 gap-4 stagger lg:grid-cols-3">
              {kits.map((p) => (
                <ProductCard
                  key={`kit-${p.id}`}
                  p={p}
                  cart={cart}
                  add={add}
                  dec={dec}
                  badge="Kit"
                  section="kits"
                  creatorSlug={creatorSlug}
                />
              ))}
            </div>
          </section>
        )}

        {bestSellers.length > 0 && activeCategory === "Todos" && !term && (
          <section className="mb-12" data-testid="catalog-bestsellers-section">
            <h2 className="mb-4 flex items-center gap-2 font-display text-base font-medium md:text-lg">
              <Flame className="h-4 w-4 text-primary" /> Destaques
            </h2>
            <div className="grid grid-cols-2 gap-4 stagger lg:grid-cols-3">
              {bestSellers.map((p) => (
                <ProductCard key={`top-${p.id}`} p={p} cart={cart} add={add} dec={dec} badge="Destaque" section="top" creatorSlug={creatorSlug} />
              ))}
            </div>
          </section>
        )}

        {visible.length === 0 && products && products.length > 0 && (
          <EmptyState
            icon={Search}
            title="Nada encontrado"
            description="Tente outra busca ou escolha outra categoria."
          />
        )}

        <div id="produtos" />
        {categories.map(([category, list]) => (
          <section key={category} className="mb-12">
            <h2 className="mb-4 font-display text-base font-medium text-muted-foreground md:text-lg">
              {category}
            </h2>
            <div className="grid grid-cols-2 gap-4 stagger lg:grid-cols-3">
              {list.map((p) => (
                <ProductCard key={p.id} p={p} cart={cart} add={add} dec={dec} creatorSlug={creatorSlug} />
              ))}
            </div>
          </section>
        ))}
        </>
        )}
        <footer
          className="mt-4 border-t border-border pt-8 text-center text-xs text-muted-foreground"
          data-testid="catalog-footer"
        >
          <p>
            Tem uma loja e quer revender nossos produtos?{" "}
            <a
              href="/sejaparceiro"
              data-testid="catalog-partner-link"
              className="font-medium text-brand transition-colors hover:text-brand/80"
            >
              {config.texts?.partner_cta_label || "Quero ser parceiro"}
            </a>
          </p>
          <p className="mt-2">{config.studio_name} · Impressão 3D · Peças autorais</p>
        </footer>
      </main>

      {count > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-card/95 px-5 py-3 backdrop-blur-xl md:hidden">
          <Button
            onClick={() => setCartOpen(true)}
            data-testid="mobile-cart-bar-btn"
            className="h-12 w-full justify-between rounded-full px-5 font-semibold"
          >
            <span>
              {count} {count === 1 ? "item" : "itens"}
            </span>
            <span className="font-num">{brl(total)}</span>
          </Button>
        </div>
      )}

      <ResponsiveModal
        open={cartOpen}
        onOpenChange={setCartOpen}
        title="Meu pedido"
        description="Revise sua seleção antes de finalizar."
        testid="cart-modal"
      >
        {items.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="cart-empty">
            Você ainda não escolheu nenhum produto.
          </p>
        ) : (
          <div className="mt-2 flex flex-col gap-4">
            <ul className="divide-y divide-border">
              {items.map(({ key, product, variation, quantity, customization }) => (
                <li key={key} className="flex items-center gap-3 py-3" data-testid={`cart-item-${key}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {product.name}
                      {variation && <span className="text-brand"> · {variation.name}</span>}
                    </p>
                    {customization?.length > 0 && (
                      <p className="truncate text-[11px] text-brand" data-testid={`cart-custom-${key}`}>
                        {customization.map((c) => `${c.name}: ${c.value}`).join(" · ")}
                      </p>
                    )}
                    <p className="font-num text-xs text-muted-foreground">
                      {quantity} × {brl(product.price)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-1.5 py-1">
                    <button onClick={() => dec(key)} data-testid={`cart-dec-${key}`} className="p-1 text-muted-foreground">
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-5 text-center font-num text-sm">{quantity}</span>
                    <button onClick={() => add(key)} data-testid={`cart-inc-${key}`} className="p-1 text-brand">
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <span className="w-20 shrink-0 text-right font-num text-sm">
                    {brl(product.price * quantity)}
                  </span>
                  <button
                    onClick={() => remove(key)}
                    data-testid={`cart-remove-${key}`}
                    className="text-muted-foreground transition-colors hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between rounded-lg border border-brand/30 bg-brand/10 px-4 py-3">
              <span className="text-sm text-muted-foreground">Total</span>
              <span className="font-num text-xl text-brand" data-testid="cart-total">
                {brl(total)}
              </span>
            </div>
            <DeliveryEstimateCard estimate={eta.estimate} loading={eta.loading} />
            <Button
              onClick={() => {
                setCartOpen(false);
                setTimeout(() => setCheckoutOpen(true), 180);
              }}
              data-testid="checkout-btn"
              className="h-12 rounded-full font-semibold transition-transform active:scale-95"
            >
              Finalizar pedido
            </Button>
            <button
              type="button"
              onClick={clearCart}
              data-testid="clear-cart-btn"
              className="mx-auto flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" /> Limpar carrinho
            </button>
          </div>
        )}
      </ResponsiveModal>

      <CheckoutModal
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        items={items}
        total={total}
        eta={eta}
        onDone={() => {
          setCart({});
          setCustomizations({});
        }}
      />
    </div>
  );
}

/** Card único do catálogo: imagem grande, cores, status, preço e ação. */
function ProductCard({ p, cart, add, dec, badge, section = "", initialVariationId = "",
                      creatorSlug = "" }) {
  const tid = section ? `${section}-${p.id}` : p.id;
  const isKit = p.kind === "kit";
  const variations = p.variations || [];
  // Com uma única combinação, ela já vem escolhida. Com duas ou mais, o cliente precisa escolher.
  // Link direto já chega com a combinação selecionada.
  const [variationId, setVariationId] = useState(
    initialVariationId || (variations.length === 1 ? variations[0].id : "")
  );
  const variation = variations.find((v) => v.id === variationId) || null;
  const needsChoice = variations.length > 1 && !variation;
  const fields = p.customizable ? p.custom_fields || [] : [];
  const [customizing, setCustomizing] = useState(false);
  const photo = variation?.photo_url || p.photo_url;
  const mosaic = isKit && !photo && p.component_photos?.length > 0;
  const available = variation ? variation.available : p.available;
  const key = cartKey(p.id, variation?.id);

  const addToCart = () => {
    if (needsChoice) {
      toast.error("Escolha a combinação de cores antes de adicionar.");
      return;
    }
    if (fields.length > 0) {
      setCustomizing(true);
      return;
    }
    add(key);
  };

  // Cada personalização gera a sua própria linha do carrinho.
  const addCustomized = (answerList) => {
    const signature = answerList.map((a) => `${a.name}=${a.value}`).join("~");
    add(signature ? `${key}|${signature}` : key, answerList);
    setCustomizing(false);
    toast.success("Produto personalizado adicionado ao pedido.");
  };

  // Compartilhar: link direto do produto no catálogo de origem, com a cor escolhida.
  const share = () => {
    const base = creatorSlug ? `/${creatorSlug}/p/${p.slug}` : `/catalogo/p/${p.slug}`;
    const color = variation?.sku || variation?.id;
    const url = `${window.location.origin}${base}${color ? `?c=${encodeURIComponent(color)}` : ""}`;
    const text = `OLHA O QUE ACHEI NO CATÁLOGO DA TR3ND!\n${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };
  return (
    <div
      className="tr-card tr-card-hover group relative overflow-hidden transition-transform hover:-translate-y-1"
      data-testid={`catalog-product-${tid}`}
    >
      {mosaic ? (
        <div
          className="grid aspect-square w-full grid-cols-2 grid-rows-2 gap-0.5 overflow-hidden bg-secondary/50"
          data-testid={`catalog-kit-composition-${tid}`}
        >
          {p.component_photos.map((url, i) => (
            <img
              key={url}
              src={mediaUrl(url)}
              alt=""
              loading="lazy"
              decoding="async"
              className={`h-full min-h-0 w-full object-cover ${
                p.component_photos.length === 1 ? "col-span-2 row-span-2 aspect-square" : ""
              } ${p.component_photos.length === 3 && i === 0 ? "col-span-2" : ""}`}
            />
          ))}
        </div>
      ) : (
        <SafeImage
          src={mediaUrl(photo)}
          alt={p.name}
          wrapperClassName="aspect-square w-full"
          loading="lazy"
        />
      )}

      {(isKit || badge) && (
        <span
          className={`absolute left-3 top-3 z-10 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
            isKit
              ? "bg-brand text-brand-foreground"
              : "bg-primary text-primary-foreground"
          }`}
          data-testid={isKit ? `catalog-kit-badge-${tid}` : `catalog-badge-${tid}`}
        >
          {isKit ? "Kit" : badge}
        </span>
      )}

      <div className="p-4">
        <p
          className="truncate font-display text-sm font-medium md:text-base"
          data-testid={`catalog-name-${tid}`}
        >
          {p.name}
        </p>
        <p className="mt-0.5 flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
          <span className="truncate">{isKit ? p.category || "Kit" : p.category || "Peça autoral"}</span>
          <button
            type="button"
            onClick={share}
            title="Compartilhar no WhatsApp"
            aria-label="Compartilhar no WhatsApp"
            data-testid={`catalog-share-${tid}`}
            className="ml-auto shrink-0 rounded-full p-1 text-muted-foreground transition-colors hover:text-brand"
          >
            <Share2 className="h-3.5 w-3.5" />
          </button>
        </p>
        {isKit && p.components?.length > 0 && (
          <p
            className="mt-1.5 line-clamp-2 text-xs text-brand"
            data-testid={`catalog-kit-components-${tid}`}
          >
            {p.components.map((c) => `${c.quantity}x ${c.name}`).join(" + ")}
          </p>
        )}
        {!isKit && p.description && (
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{p.description}</p>
        )}

        {variations.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid={`catalog-variations-${tid}`}>
            {variations.map((v) => (
              <button
                key={v.id}
                onClick={() => setVariationId((current) => (current === v.id ? "" : v.id))}
                data-testid={`catalog-variation-${tid}-${v.id}`}
                className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                  v.id === variationId
                    ? "border-brand bg-brand/15 text-brand"
                    : "border-white/15 text-muted-foreground hover:text-foreground"
                }`}
              >
                {v.name}
              </button>
            ))}
          </div>
        )}

        <span
          className={`tr-status mt-2.5 ${
            needsChoice ? "tr-status-info" : available ? "tr-status-success" : "tr-status-warning"
          }`}
          data-testid={`catalog-availability-${tid}`}
        >
          {needsChoice ? "Escolha a combinação" : available ? "Pronta entrega" : "Sob encomenda"}
        </span>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <span
            className="font-num text-base text-foreground md:text-lg"
            data-testid={`catalog-price-${tid}`}
          >
            {brl(p.price)}
          </span>
          {cart[key] ? (
            <div className="flex items-center gap-1.5 rounded-full border border-brand/30 bg-brand/10 px-1.5 py-1">
              <button
                onClick={() => dec(key)}
                data-testid={`catalog-dec-${tid}`}
                className="rounded-full p-1 text-muted-foreground transition-colors hover:text-foreground"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="w-5 text-center font-num text-sm" data-testid={`catalog-qty-${tid}`}>
                {cart[key]}
              </span>
              <button
                onClick={() => add(key)}
                data-testid={`catalog-inc-${tid}`}
                className="rounded-full p-1 text-brand transition-colors hover:text-brand/80"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>            </div>
          ) : (
            <Button
              size="sm"
              onClick={addToCart}
              data-testid={`catalog-add-${tid}`}
              className="h-9 gap-1.5 rounded-full px-3 text-[11px] font-semibold transition-transform active:scale-95 md:px-4 md:text-xs"
            >
              {fields.length > 0 ? (
                <>
                  <Sparkles className="h-3.5 w-3.5" /> Personalizar
                </>
              ) : (
                "Adicionar"
              )}
            </Button>
          )}
        </div>
      </div>
      {customizing && (
        <CustomizeModal
          product={p}
          variation={variation}
          price={p.price}
          onClose={() => setCustomizing(false)}
          onAdd={addCustomized}
        />
      )}
    </div>
  );
}

const EMPTY_CHECKOUT = { customer_name: "", ddd: "", phone: "", notes: "", city: "",
                         cep: "", street: "", number: "", district: "", complement: "",
                         reference: "", delivery_notes: "" };

const onlyDigits = (value) => (value || "").replace(/\D/g, "");

/** Telefone BR: DDD de 2 dígitos (11 a 99) + 8 ou 9 dígitos. */
const validPhone = (ddd, phone) =>
  /^[1-9][0-9]$/.test(onlyDigits(ddd)) && /^[0-9]{8,9}$/.test(onlyDigits(phone));

function CheckoutModal({ open, onOpenChange, items, total, eta, onDone }) {
  const [form, setForm] = useState(EMPTY_CHECKOUT);
  const [method, setMethod] = useState("RETIRADA");
  const [shipping, setShipping] = useState("CORREIOS");
  const [paymentChoice, setPaymentChoice] = useState("RETIRADA");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [proc, setProc] = useState({ active: false, done: false, error: "", data: null });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open) {
      // Dados do comprador guardados no próprio navegador dele, sem token nem nada sensível.
      setForm({ ...EMPTY_CHECKOUT, ...readStore(BUYER_KEY, {}) });
      setMethod("RETIRADA");
      setShipping("CORREIOS");
      setDone(null);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim())
      return toast.error("Informe seu nome completo.");
    if (!validPhone(form.ddd, form.phone))
      return toast.error("Telefone inválido: confira o DDD e o número do WhatsApp.");
    if (!form.city.trim()) return toast.error("Informe sua cidade.");
    if (method === "ENTREGA") {
      if (onlyDigits(form.cep).length !== 8)
        return toast.error("CEP inválido: informe os 8 dígitos.");
      for (const [key, label] of [["street", "a rua/avenida"], ["number", "o número"],
                                  ["district", "o bairro"]]) {
        if (!form[key].trim()) return toast.error(`Informe ${label} da entrega.`);
      }
    }
    setBusy(true);
    setProc({ active: true, done: false, error: "", data: null });
    try {
      const { data } = await publicApi.post("/public/orders", {
        customer_name: form.customer_name,
        notes: form.notes,
        // Brasil (+55) é fixo: enviamos DDD + número e o backend normaliza.
        phone: `(${form.ddd}) ${form.phone}`,
        payment_choice: paymentChoice,
        receiving: {
          method,
          city: form.city,
          cep: method === "ENTREGA" ? form.cep : "",
          street: method === "ENTREGA" ? form.street : "",
          number: method === "ENTREGA" ? form.number : "",
          district: method === "ENTREGA" ? form.district : "",
          complement: method === "ENTREGA" ? form.complement : "",
          reference: method === "ENTREGA" ? form.reference : "",
          notes: method === "ENTREGA" ? form.delivery_notes : "",
          shipping_preference: method === "ENTREGA" ? shipping : "",
        },
        items: items.map((i) =>
          i.product.kind === "kit"
            ? {
                kit_id: i.product.id,
                variation_id: i.variation?.id || null,
                quantity: i.quantity,
              }
            : {
                product_id: i.product.id,
                variation_id: i.variation?.id || null,
                quantity: i.quantity,
                customization: i.customization || [],
              }
        ),
      });
      setProc({ active: true, done: true, error: "", data });
      writeStore(BUYER_KEY, { ...form, notes: "", delivery_notes: "" });
    } catch (err) {
      setProc({ active: true, done: false, error: apiError(err), data: null });
    } finally {
      setBusy(false);
    }
  };

  const finishProcessing = () => {
    const data = proc.data;
    setProc({ active: false, done: false, error: "", data: null });
    setDone(data);
    onDone();
    if (data.whatsapp_url) window.open(data.whatsapp_url, "_blank");
    toast.success("Pedido registrado! Finalize a conversa no WhatsApp.");
  };

  return (
    <>
    {proc.active && (
      <ProcessingOverlay
        title="Finalizando seu pedido..."
        steps={[
          "Conferindo produtos",
          "Verificando disponibilidade",
          "Registrando seu pedido",
          "Preparando sua produção",
        ]}
        done={proc.done}
        error={proc.error}
        successTitle="Pedido recebido!"
        successText="Obrigado pela compra!"
        onFinish={finishProcessing}
        onClose={() => setProc({ active: false, done: false, error: "", data: null })}
        testid="checkout-processing"
      />
    )}
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={done ? "Pedido enviado!" : "Seus dados"}
      description={
        done
          ? "Seu pedido ficou registrado como pendente de pagamento."
          : "Seus dados de contato e como você quer receber o pedido."
      }
      testid="checkout-modal"
    >
      {done ? (
        <div className="mt-2 flex flex-col gap-4" data-testid="checkout-success">
          <div className="rounded-lg border border-border bg-secondary/40 p-4 text-sm">
            <p className="font-num text-2xl text-brand">{brl(done.order.total)}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Pedido de {done.order.customer_name} ·{" "}
              {done.order.items.reduce((s, i) => s + i.quantity, 0)}{" "}
              {done.order.items.reduce((s, i) => s + i.quantity, 0) === 1 ? "item" : "itens"}
            </p>
          </div>
          {done.pix_path && (
            <Button asChild className="h-12 rounded-full font-semibold">
              <a href={done.pix_path} data-testid="checkout-pix-link">
                💰 Pagar via Pix
              </a>
            </Button>
          )}
          {done.whatsapp_url ? (
            <Button asChild variant={done.pix_path ? "outline" : "default"} className="h-12 rounded-full font-semibold">
              <a href={done.whatsapp_url} target="_blank" rel="noreferrer" data-testid="whatsapp-link">
                Abrir conversa no WhatsApp
              </a>
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">
              O WhatsApp da loja ainda não foi configurado. Entre em contato pelas redes da loja
              informando o número do seu pedido.
            </p>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="close-checkout-btn">
            Fechar
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
          <Field label="Seu nome">
            <Input value={form.customer_name} onChange={set("customer_name")} required placeholder="Ex.: João" data-testid="checkout-name-input" />
          </Field>
          <div className="grid grid-cols-[92px_1fr] gap-3">
            <Field label="DDD">
              <Input
                value={form.ddd}
                onChange={(e) =>
                  setForm((f) => ({ ...f, ddd: e.target.value.replace(/\D/g, "").slice(0, 2) }))
                }
                required
                inputMode="numeric"
                maxLength={2}
                placeholder="51"
                data-testid="checkout-ddd-input"
                className="font-num"
              />
            </Field>
            <Field label="WhatsApp">
              <Input
                value={form.phone}
                onChange={set("phone")}
                required
                inputMode="tel"
                placeholder="99999-9999"
                data-testid="checkout-phone-input"
                className="font-num"
              />
            </Field>
          </div>
          <p className="-mt-2 text-[11px] text-muted-foreground">
            Brasil (+55) já está incluído — informe só o DDD e o número.
          </p>
          <Field label="Cidade">
            <Input value={form.city} onChange={set("city")} required
                   placeholder="Ex.: Porto Alegre" data-testid="checkout-city-input" />
          </Field>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Como você quer receber?
            </span>
            <div className="grid grid-cols-2 gap-2">
              {[["RETIRADA", "Retirada"], ["ENTREGA", "Entrega no endereço"]].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMethod(key)}
                  data-testid={`checkout-method-${key.toLowerCase()}`}
                  className={`rounded-xl border p-3 text-left text-xs transition-colors ${
                    method === key
                      ? "border-brand bg-brand/10 text-foreground"
                      : "border-border bg-secondary/30 text-muted-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {method === "RETIRADA" ? (
            <p className="rounded-xl border border-border bg-secondary/40 p-3 text-[11px] font-semibold uppercase tracking-wide"
               data-testid="checkout-pickup-notice">
              Após a finalização do pedido, confirmaremos via WhatsApp o horário para retirada.
            </p>
          ) : (
            <div className="flex flex-col gap-3" data-testid="checkout-delivery-fields">
              <div className="grid grid-cols-2 gap-3">
                <Field label="CEP">
                  <Input value={form.cep}
                         onChange={(e) => setForm((f) => ({
                           ...f, cep: e.target.value.replace(/[^\d-]/g, "").slice(0, 9) }))}
                         required inputMode="numeric" placeholder="90000-000"
                         data-testid="checkout-cep-input" className="font-num" />
                </Field>
                <Field label="Número">
                  <Input value={form.number} onChange={set("number")} required
                         placeholder="123" data-testid="checkout-number-input" />
                </Field>
              </div>
              <Field label="Rua/Avenida">
                <Input value={form.street} onChange={set("street")} required
                       placeholder="Av. Exemplo" data-testid="checkout-street-input" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Bairro">
                  <Input value={form.district} onChange={set("district")} required
                         placeholder="Centro" data-testid="checkout-district-input" />
                </Field>
                <Field label="Complemento">
                  <Input value={form.complement} onChange={set("complement")}
                         placeholder="Apto 101" data-testid="checkout-complement-input" />
                </Field>
              </div>
              <Field label="Ponto de referência">
                <Input value={form.reference} onChange={set("reference")}
                       placeholder="Próximo à praça" data-testid="checkout-reference-input" />
              </Field>
              <Field label="Observações para a entrega">
                <Textarea value={form.delivery_notes} onChange={set("delivery_notes")} rows={2}
                          placeholder="Horário preferido, portaria..."
                          data-testid="checkout-delivery-notes-input" />
              </Field>
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Opção de entrega
                </span>
                <div className="grid grid-cols-2 gap-2">
                  {[["CORREIOS", "Correios"], ["MOTOBOY", "Motoboy"]].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setShipping(key)}
                      data-testid={`checkout-shipping-${key.toLowerCase()}`}
                      className={`rounded-xl border p-3 text-left text-xs transition-colors ${
                        shipping === key
                          ? "border-brand bg-brand/10 text-foreground"
                          : "border-border bg-secondary/30 text-muted-foreground"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground"
                   data-testid="checkout-shipping-notice">
                  O custo da entrega será confirmado pelo WhatsApp. A opção escolhida é uma
                  preferência e depende da confirmação da TR3ND para a sua cidade.
                </p>
              </div>
            </div>
          )}

          <Field label="Observações">
            <Textarea value={form.notes} onChange={set("notes")} rows={2} placeholder="Cores, prazos, personalizações..." data-testid="checkout-notes-input" />
          </Field>
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Como você prefere pagar?
            </span>
            <div className="grid grid-cols-2 gap-2">
              {[
                ["RETIRADA", "Pagar na retirada/entrega"],
                ["PIX", "Pagar agora via Pix"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setPaymentChoice(key)}
                  data-testid={`checkout-payment-${key.toLowerCase()}`}
                  className={`rounded-xl border p-3 text-left text-xs transition-colors ${
                    paymentChoice === key
                      ? "border-brand bg-brand/10 text-brand"
                      : "border-white/15 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Nada é cobrado agora. Se escolher Pix, mostramos o QR Code depois de registrar o
              pedido — você pode pagar quando quiser.
            </p>
          </div>
          <DeliveryEstimateCard estimate={eta?.estimate} loading={eta?.loading}
                                testid="checkout-delivery-estimate" />
          <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 px-4 py-3 text-sm">
            <span className="text-muted-foreground">Total do pedido</span>
            <span className="font-num text-lg text-brand" data-testid="checkout-total">
              {brl(total)}
            </span>
          </div>
          <Button type="submit" disabled={busy} data-testid="confirm-order-btn" className="h-12 rounded-full font-semibold transition-transform active:scale-95">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirmar e enviar no WhatsApp
          </Button>
        </form>
      )}
    </ResponsiveModal>
    </>
  );
}
